import { Component, DestroyRef, inject } from '@angular/core';
import { IekConfigurationModel } from '@configuration/wizard/models/types/iek-configuration.model';
import { LtlWizardModel } from '@configuration/wizard/models/types/ltl-wizard-model';
import { ConfigurationWizardService } from './service/configuration-wizard.service';
import { WizardModel } from './models/wizard-model';
import { ConfigurationTemplate, ConfigurationTemplateGroup } from './models/wizard-model.interface';
import { MiniRailModel } from './models/assistants/mini-rail.model';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ControlCabinetModel } from './models/types/control-cabinet.model';
import { MarkingCardsModel } from './models/types/marking-cards.model';
import { MCSModel } from './models/types/mcs-model';
import { GDSModel } from './models/types/gds-model';
import { McsMaxiModel } from './models/assistants/mcs-maxi.model';
import { UserModel } from '@user/model/user.model';
import { ComponentHotkeyService } from '@shared/component-hotkey.service';
import { DistributionTerminalsWizardModel } from './models/assistants/distribution-terminals-wizard-model';
import { UserPermissionName } from '@user/constants/user-permissions';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { IoSystemsModel } from './models/types/io-systems.model';
import { SplicingConnector221Model } from './models/types/splicing-connector221-model';

@Component({
  standalone: false,
  selector: 'app-configuration-wizard',
  templateUrl: './configuration-wizard.component.html',
  styleUrls: ['./configuration-wizard.component.scss'],
  providers: [
    ControlCabinetModel,
    MCSModel,
    GDSModel,
    MarkingCardsModel,
    MiniRailModel,
    McsMaxiModel,
    ComponentHotkeyService,
    DistributionTerminalsWizardModel,
    IoSystemsModel,
    IekConfigurationModel,
    SplicingConnector221Model,
    LtlWizardModel,
  ],
})
export class ConfigurationWizardComponent {
  private _assistantsWizards: WizardModel[];
  private _configurationTypeWizards: WizardModel[];

  private readonly _templates: [ ConfigurationTemplateGroup, ConfigurationTemplateGroup ];

  public openedFromDashboard?: boolean;

  private readonly wizardService = inject(ConfigurationWizardService);
  public currentWizard = toSignal(this.wizardService.currentWizard$);

  constructor(
    private readonly bsModalRef: BsModalRef,
    controlCabinetModel: ControlCabinetModel,
    mcsModel: MCSModel,
    gdsModel: GDSModel,
    markingCardsModel: MarkingCardsModel,
    miniRailModel: MiniRailModel,
    mcsMaxiModel: McsMaxiModel,
    distributionTerminalsWizardModel: DistributionTerminalsWizardModel,
    splicingConnector221Model: SplicingConnector221Model,
    ltlWizardModel: LtlWizardModel,
    ioSystemsModel: IoSystemsModel,
    iekConfigurationModel: IekConfigurationModel,
    private readonly destroyRef: DestroyRef,
    private readonly userModel: UserModel,
    private readonly componentHotkeyService: ComponentHotkeyService,
  ) {
    this._configurationTypeWizards = [ controlCabinetModel, mcsModel, gdsModel, splicingConnector221Model, markingCardsModel ];
    this._assistantsWizards = [ioSystemsModel, iekConfigurationModel, miniRailModel, mcsMaxiModel, distributionTerminalsWizardModel, ltlWizardModel];

    this.wizardService.reset();
    this._templates = this.loadTemplates();

    this.componentHotkeyService.add('enter', async () => {
      if (this.wizardService.submitEnabled) {
        await this.nextStep();
      }
    }, {allowIn: ['INPUT']});
  }

  public get templates(): ConfigurationTemplateGroup[] {
    return this._templates;
  }

  public get selectedTemplateTitle(): string | undefined {
    return this.configuration?.label;
  }

  public get configuration(): ConfigurationTemplate | undefined {
    return this.wizardService.currentWizard?.config;
  }

  public get submitEnabled(): boolean {
    return this.wizardService.submitEnabled;
  }

  public get isBackEnabled(): boolean {
    return this.wizardService.currentWizard !== null ? this.wizardService.currentWizard.currentStepIndex > 0 : false;
  }

  public isLastStep(): boolean {
    return this.wizardService.currentWizard !== null
      ? this.wizardService.currentWizard.currentStepIndex === this.wizardService.currentWizard.config.steps.length - 1
      : false;
  }

  private loadTemplates(): [ ConfigurationTemplateGroup, ConfigurationTemplateGroup ] {
    return [
      {
        label: 'configurationCreation.types',
        templates: this._configurationTypeWizards.map((wizard) => wizard.config).filter((config) => this.checkPermission(config.neededPermission)),
      },
      {
        label: 'configurationCreation.assistants',
        templates: this._assistantsWizards.map((wizard) => wizard.config).filter((config) => this.checkPermission(config.neededPermission)),
      },
    ];
  }

  private checkPermission(neededPermission?: UserPermissionName): boolean {
    if (!neededPermission) {
      return true;
    }
    return this.userModel.user && this.userModel.user.hasPermissions([neededPermission]);
  }

  public abort(): void {
    this.bsModalRef.hide();
  }

  protected startConfiguration(configuration: ConfigurationTemplate): void {
    const wizardModel = [...this._configurationTypeWizards, ...this._assistantsWizards]
      .find((wizard) => wizard.config.id === configuration.id);
    if (!wizardModel) {
      return;
    }
    this.startConfigurationWizard(wizardModel);
  }

  public startConfigurationWizard(wizardModel: WizardModel): void {
    this.wizardService.start(wizardModel);
    this.wizardService.finishedCreation$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.abort());
  }

  public async previousStep(): Promise<void> {
    await this.wizardService.previousStep();
  }

  public async nextStep(): Promise<void> {
    await this.wizardService.nextStep();
  }
}
